import { SpinnerOverlay } from '@porsche-code/kado-ui';
import {
  componentsReady,
  PorscheDesignSystemProvider,
} from '@porsche-design-system/components-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ClientError } from 'graphql-request';
import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';

import App from './App';
import {
  AbilityProvider,
  PersonProvider,
  SupportRequestFilterProvider,
} from './context';

import './index.css';
import './lib/i18n';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (error instanceof ClientError) {
          if (error.response.status === 401) {
            // retry auth errors
            return failureCount < 3;
          }
          if (error.response.status >= 400 && error.response.status < 500) {
            // Do not retry client-side errors (wrong variables, etc.)
            return false;
          }
        }
        return failureCount < 3;
      },
    },
    mutations: {
      retry: (failureCount, error) => {
        if (error instanceof ClientError) {
          if (error.response.status === 401) {
            // retry auth errors
            return failureCount < 3;
          }
        }
        return false;
      },
    },
  },
});

const container = document.getElementById('root');
const root = createRoot(container!);

root.render(
  <StrictMode>
    <PorscheDesignSystemProvider>
      <Suspense fallback={<SpinnerOverlay size="2xl" />}>
        <PersonProvider>
          <SupportRequestFilterProvider>
            <AbilityProvider>
              <QueryClientProvider client={queryClient}>
                <App />
              </QueryClientProvider>
            </AbilityProvider>
          </SupportRequestFilterProvider>
        </PersonProvider>
      </Suspense>
    </PorscheDesignSystemProvider>
  </StrictMode>,
);

// https://github.com/porsche-design-system/porsche-design-system/blob/main/packages/components-react/src/main.tsx
(
  window as unknown as Window & { componentsReady: typeof componentsReady }
).componentsReady = componentsReady;
